import React, { ReactElement, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { GamprotectTabState } from './GamprotectTab.state';
import { PageContainer } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs.style';
import { LoadingV2 } from 'src/components/Loading/LoadingV2';
import { BadgeColored } from 'src/components/CustomersBar/CustomerTab/CustomerSummaryTab/CustomerSummaryTab.style';
import { GamprotectMarketsTable } from './GamprotectMarketsTable';
import { Button } from 'src/domains/common/components/button/button';
import { Alert } from 'src/domains/common/components/alert/alert';

const renderButton = (state: GamprotectTabState): ReactElement | null => {
    const possibleAction = state.possibleAction;

    if (possibleAction === null) {
        return null;
    }

    return (
        <div className='relative inline-block group'>
            <Button
                icon={possibleAction.type === 'remove' ? 'trash' : 'plus'}
                onClick={state.handleButtonClick}
                variant={possibleAction.type === 'remove' ? 'destructive' : 'primary'}
                disabled={!possibleAction.isAllowed}
                data-tooltip-target='tooltip-default'
            >
                {possibleAction.type === 'remove' ? 'Remove from Gamprotect' : 'Submit to Gamprotect'}
            </Button>

            {possibleAction.message !== undefined && (
                <div className='absolute w-48 top-full left-1/2 transform -translate-x-1/2 mt-3 px-2 py-2 bg-gray-700 text-black text-xs rounded invisible group-hover:visible transition-all duration-300 pointer-events-none z-50 shadow-md text-center font-medium text-white'>
                    {possibleAction.message}
                </div>
            )}
        </div>
    );
};

interface GamprotectPropsType {
    customerId: number;
}

export const GamprotectTab = observer('GamprotectTab', ({ customerId }: GamprotectPropsType) => {
    const { models, common, permissionGrantsState } = useAppStateContext();

    const [state] = useState(() => new GamprotectTabState(customerId, models, permissionGrantsState, common));
    const gamprotectMarkers = state.gamprotectUserStore.markers;
    if (gamprotectMarkers === 'error') {
        return (
            <Alert
                title='Error Occured'
                variant='destructive'
                icon='shield'
            >
                We cannot retrieve the data. Please try again.
            </Alert>
        );
    }

    if (gamprotectMarkers === 'loading') {
        return (
            <LoadingV2
                dataTest='loading'
                title='Loading Gamprotect data...'
            />
        );
    }

    return (
        <PageContainer>
            <header className='flex items-center justify-between flex-[0_0_36px]'>
                <h2
                    className='header text-foreground'
                    data-test='page-title'
                >
                    Gamprotect
                </h2>
                {renderButton(state)}
            </header>
            <h4
                className='flex items-center flex gap-2 mt-0 mb-10'
                data-test='status'
            >
                <span>Active:</span>
                <BadgeColored
                    badgeColor={state.gamprotectUserStore.hasGamprotect === true ? 'red' : 'green'}
                    data-test='gamprotect-status'
                >
                    {state.gamprotectUserStore.hasGamprotect === true ? 'Yes' : 'No'}
                </BadgeColored>
            </h4>

            {state.gamprotectUserStore.hasGamprotect === false ? (
                <Alert
                    title='No Active Markers'
                    variant='default'
                    icon='shield'
                >
                    The customer does not have active markers.
                </Alert>
            ) : null}

            {gamprotectMarkers.length > 0 ? (
                <>
                    {state.gamprotectUserStore.hasGamprotect === false && <h4 className='mt-16 mb-6 py-0'>History</h4>}
                    <GamprotectMarketsTable markers={gamprotectMarkers} />
                </>
            ) : null}
        </PageContainer>
    );
});
